import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../config/axios';

const GestionSolicitudesRol = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [processingAction, setProcessingAction] = useState(false);
  const [accionSeleccionada, setAccionSeleccionada] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const fetchSolicitudes = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/usuarios/solicitudes-cambio-rol');
      if (response.data) {
        setSolicitudes(response.data);
      } else {
        setError('No se recibieron datos del servidor');
      }
    } catch (error) {
      console.error('Error completo:', error);
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Error al cargar las solicitudes';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSolicitudes();
  }, []);

  const handleProcesarSolicitud = async (estado) => {
    setProcessingAction(true);
    try {
      await axiosInstance.put(
        `/usuarios/solicitudes-cambio-rol/${selectedSolicitud._id}`,
        { estado }
      );
      
      enqueueSnackbar(
        `Solicitud ${estado === 'aprobada' ? 'aprobada' : 'rechazada'} exitosamente`,
        { variant: 'success' }
      );
      
      setSolicitudes(prevSolicitudes =>
        prevSolicitudes.filter(s => s._id !== selectedSolicitud._id)
      );
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || 'Error al procesar la solicitud',
        { variant: 'error' }
      );
    } finally {
      setProcessingAction(false);
      setDialogOpen(false);
      setSelectedSolicitud(null);
      setAccionSeleccionada('');
    }
  };

  const handleOpenDialog = (solicitud, accion) => {
    setSelectedSolicitud(solicitud);
    setAccionSeleccionada(accion);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        Gestión de Solicitudes de Cambio de Rol
      </Typography>

      {solicitudes.length === 0 ? (
        <Alert severity="info">
          No hay solicitudes pendientes de revisión.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Especialidad</TableCell>
                <TableCell>Institución</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {solicitudes.map((solicitud) => (
                <TableRow key={solicitud._id}>
                  <TableCell>
                    {`${solicitud.nombre} ${solicitud.apellidos}`}
                  </TableCell>
                  <TableCell>{solicitud.email}</TableCell>
                  <TableCell>{solicitud.solicitudCambioRol.especialidad}</TableCell>
                  <TableCell>{solicitud.solicitudCambioRol.institucionEducativa}</TableCell>
                  <TableCell>
                    {new Date(solicitud.solicitudCambioRol.fecha).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => handleOpenDialog(solicitud, 'aprobar')}
                      >
                        Aprobar
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleOpenDialog(solicitud, 'rechazar')}
                      >
                        Rechazar
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => !processingAction && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {accionSeleccionada === 'aprobar' ? 'Aprobar Solicitud' : 'Rechazar Solicitud'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText paragraph>
            ¿Estás seguro de que deseas {accionSeleccionada} la siguiente solicitud?
          </DialogContentText>
          
          {selectedSolicitud && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Solicitante"
                          secondary={`${selectedSolicitud.nombre} ${selectedSolicitud.apellidos}`}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText 
                          primary="Email"
                          secondary={selectedSolicitud.email}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText 
                          primary="Especialidad"
                          secondary={selectedSolicitud.solicitudCambioRol.especialidad}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText 
                          primary="Registro Médico"
                          secondary={selectedSolicitud.solicitudCambioRol.numeroRegistroMedico}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText 
                          primary="Institución Educativa"
                          secondary={selectedSolicitud.solicitudCambioRol.institucionEducativa}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText 
                          primary="Año de Graduación"
                          secondary={selectedSolicitud.solicitudCambioRol.anioGraduacion}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText 
                          primary="Experiencia"
                          secondary={selectedSolicitud.solicitudCambioRol.experiencia}
                        />
                      </ListItem>
                      {selectedSolicitud.solicitudCambioRol.mensaje && (
                        <>
                          <Divider />
                          <ListItem>
                            <ListItemText 
                              primary="Mensaje Adicional"
                              secondary={selectedSolicitud.solicitudCambioRol.mensaje}
                            />
                          </ListItem>
                        </>
                      )}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDialogOpen(false)} 
            disabled={processingAction}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleProcesarSolicitud(accionSeleccionada === 'aprobar' ? 'aprobada' : 'rechazada')}
            color={accionSeleccionada === 'aprobar' ? 'success' : 'error'}
            variant="contained"
            disabled={processingAction}
            autoFocus
          >
            {processingAction ? (
              <CircularProgress size={24} />
            ) : (
              accionSeleccionada === 'aprobar' ? 'Aprobar' : 'Rechazar'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionSolicitudesRol; 